import { useState } from "react";
import { API_URL } from "../../config";

interface Course {
  id: string;
  title: string;
}

interface CertificateViewProps {
  course: Course;
  onBack: () => void;
}

interface VerifyResult {
  verified: boolean;
  certificate_id?: number;
  student_name?: string;
  student_email?: string;
  course_name?: string;
  issued_at_readable?: string;
  contract_address: string;
  network: string;
  etherscan_contract: string;
}

export default function CertificateView({
  course,
  onBack,
}: CertificateViewProps) {
  const [studentName, setStudentName] = useState(
    () => localStorage.getItem("student_name") || ""
  );
  const [studentEmail, setStudentEmail] = useState(
    () => localStorage.getItem("student_email") || ""
  );
  const [certHash, setCertHash] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [blockchainResult, setBlockchainResult] = useState<{
    transaction_hash: string;
    etherscan_url: string;
  } | null>(null);
  const [blockchainError, setBlockchainError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<VerifyResult | null>(null);
  const [verifyError, setVerifyError] = useState("");
  const [error, setError] = useState("");

  const completionDate = new Date().toISOString().split("T")[0];

  const handleDownload = async () => {
    if (!studentName.trim()) { setError("Please enter your name"); return; }
    if (!studentEmail.trim()) { setError("Please enter your email"); return; }
    setError("");
    setBlockchainError("");
    setDownloading(true);
    localStorage.setItem("student_name", studentName);
    localStorage.setItem("student_email", studentEmail);

    try {
      const resp = await fetch(`${API_URL}/api/course/certificate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          student_name: studentName,
          student_email: studentEmail,
          course_title: course.title,
          completion_date: completionDate,
          course_id: course.id,
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ message: "Certificate generation failed" }));
        setError(err.message || "Certificate generation failed");
        return;
      }

      const hash = resp.headers.get("X-Certificate-Hash") || "";
      const txHash = resp.headers.get("X-Transaction-Hash") || "";
      const etherscanUrl = resp.headers.get("X-Etherscan-URL") || "";
      const chainErr = resp.headers.get("X-Blockchain-Error") || "";

      setCertHash(hash);
      setVerifyResult(null);

      if (txHash && etherscanUrl) {
        setBlockchainResult({ transaction_hash: txHash, etherscan_url: etherscanUrl });
      } else if (chainErr) {
        setBlockchainError(chainErr);
      }

      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificate_${studentName.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e.message || "Failed to download certificate");
    } finally {
      setDownloading(false);
    }
  };

  const handleVerify = async () => {
    if (!certHash) return;
    setVerifying(true);
    setVerifyError("");
    setVerifyResult(null);
    try {
      const resp = await fetch(`${API_URL}/api/course/certificate/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          certificate_hash: certHash,
          transaction_hash: blockchainResult?.transaction_hash ?? "",
        }),
      });
      const data = await resp.json();
      if (data.status === "success") {
        setVerifyResult(data as VerifyResult);
      } else {
        setVerifyError(data.message || "Verification failed");
      }
    } catch (e: any) {
      setVerifyError(e.message || "Verification request failed");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="certificate-view">
      <div className="congrats-banner">
        <h2>Course Complete!</h2>
        <p>You have completed all modules in this course.</p>
      </div>

      <div className="cert-preview">
        <div className="cert-brand">RENSHU</div>
        <div className="cert-title">CERTIFICATE OF COMPLETION</div>
        <div className="cert-subtitle">This is awarded to</div>
        <div className="cert-name">{studentName || "Your Name"}</div>
        <div className="cert-subtitle">for successfully completing the course</div>
        <div className="cert-course">{course.title}</div>
        <div className="cert-date">Completed on {completionDate}</div>
      </div>

      <div className="cert-name-input">
        <input
          type="text"
          placeholder="Enter your full name"
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
        />
        <input
          type="email"
          placeholder="Enter your email (stored on-chain)"
          value={studentEmail}
          onChange={(e) => setStudentEmail(e.target.value)}
          style={{ marginTop: "0.5rem" }}
        />
      </div>

      <div className="cert-actions">
        <button className="download-btn" onClick={handleDownload} disabled={downloading}>
          {downloading ? "Generating & Recording on Chain..." : "Download Certificate"}
        </button>
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* ── Blockchain confirmation ── */}
      {blockchainResult && (
        <div className="blockchain-result">
          <div className="blockchain-result-header">
            <span className="chain-badge">⛓ Sepolia Testnet</span>
            <span className="chain-confirmed">Confirmed on-chain</span>
          </div>

          <div className="chain-detail-row">
            <span className="chain-label">TX Hash</span>
            <span className="chain-value mono">{blockchainResult.transaction_hash}</span>
          </div>

          <div className="chain-detail-row">
            <span className="chain-label">Cert Hash</span>
            <span className="chain-value mono">{certHash}</span>
          </div>

          <div className="chain-links">
            <a
              href={blockchainResult.etherscan_url}
              target="_blank"
              rel="noopener noreferrer"
              className="etherscan-link"
            >
              View Transaction on Etherscan ↗
            </a>
          </div>

          <p className="chain-explain">
            The SHA-256 fingerprint of your certificate PDF is permanently stored in the
            <strong> CertificateRegistry</strong> smart contract. Anyone with your PDF can
            hash it and call <code>verifyByHash()</code> on-chain to confirm authenticity —
            no Pinata, no centralised storage needed.
          </p>

          <button
            className="verify-btn"
            onClick={handleVerify}
            disabled={verifying}
          >
            {verifying ? "Waiting for block confirmation..." : "Verify on Chain Now"}
          </button>
        </div>
      )}

      {!blockchainResult && blockchainError && (
        <div className="error-msg" style={{ marginTop: "0.5rem" }}>
          Blockchain recording failed: {blockchainError}
        </div>
      )}

      {/* ── Verification result ── */}
      {verifyResult && (
        <div className={`verify-result ${verifyResult.verified ? "verify-ok" : "verify-fail"}`}>
          <div className="verify-status">
            {verifyResult.verified ? "✅ Certificate is VALID on-chain" : "❌ Hash NOT found on-chain"}
          </div>
          {verifyResult.verified && verifyResult.certificate_id !== undefined && (
            <div className="verify-details">
              <div className="chain-detail-row">
                <span className="chain-label">Certificate ID</span>
                <span className="chain-value">#{verifyResult.certificate_id}</span>
              </div>
              <div className="chain-detail-row">
                <span className="chain-label">Issued to</span>
                <span className="chain-value">{verifyResult.student_name}</span>
              </div>
              <div className="chain-detail-row">
                <span className="chain-label">Course</span>
                <span className="chain-value">{verifyResult.course_name}</span>
              </div>
              <div className="chain-detail-row">
                <span className="chain-label">Issued at</span>
                <span className="chain-value">{verifyResult.issued_at_readable}</span>
              </div>
              <div className="chain-detail-row">
                <span className="chain-label">Network</span>
                <span className="chain-value">{verifyResult.network}</span>
              </div>
              <a
                href={verifyResult.etherscan_contract}
                target="_blank"
                rel="noopener noreferrer"
                className="etherscan-link"
              >
                Read Contract on Etherscan ↗
              </a>
            </div>
          )}
        </div>
      )}

      {verifyError && (
        <div className="error-msg" style={{ marginTop: "0.5rem" }}>
          {verifyError}
        </div>
      )}

      <button className="course-back-link" onClick={onBack} style={{ marginTop: "1.5rem" }}>
        Back to Roadmap
      </button>
    </div>
  );
}
